import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Table from "@shared/components/Table";
import ActionButtons from "@shared/components/ActionButtons";
import {
    approveNoiNotify,
    confirmNoiNotify,
    exportNoiNotify,
    formationNoiNotify,
    fetchNoiNotifies,
    openReport,
    navigateToNraReportsTable,
} from "../services/actions";

const icons = [
    { id: 'formation', image: '/images/narIcons/formation.png', labelKey: 'staff.register.nra.formation' },
    { id: 'printer', image: '/images/narIcons/printer.png', labelKey: 'staff.register.nra.printer' },
    { id: 'fileSave', image: '/images/narIcons/fileSave.png', labelKey: 'staff.register.nra.fileSave' },
    { id: 'approveDoc', image: '/images/narIcons/approveDoc.png', labelKey: 'staff.register.nra.approveDoc' },
    { id: 'groupApprove', image: '/images/narIcons/groupApprove.png', labelKey: 'staff.register.nra.groupApproval' },
    { id: 'report', image: '/images/narIcons/report.png', labelKey: 'staff.register.nra.report' },
];

export default function ElectronicWork() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [data, setData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);
    const [electronicRecordType, setElectronicRecordType] = useState('');
    const [searchQuery, setSearchQuery] = useState({});
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('');

    const excludedType = electronicRecordType !== '123';

    const defaultErrorMessage = t('common.errorMessage');

    const handleRowClick = (index) => {
        setSelectedRecord(data[index] ?? null);
    };

    const buildNoiNotifiesParams = useCallback((query, recordType, paginationOverrides = {}) => {
        const params = {};
        if (recordType === '123') params.old = true;
        if (query?.fromDate) params.fromDate = query.fromDate;
        if (query?.toDate) params.toDate = query.toDate;
        const statusVal = query?.status;
        if (statusVal === 'staff.register.nra.statusPreparing') params.status = 0;
        else if (statusVal === 'staff.register.nra.statusSubmitted') params.status = 1;
        params.page = paginationOverrides.page ?? pageNumber;
        params.size = paginationOverrides.size ?? pageSize;
        if (paginationOverrides.sortBy || sortBy) params.sortBy = paginationOverrides.sortBy || sortBy;
        if (paginationOverrides.sortOrder || sortOrder) params.sortOrder = paginationOverrides.sortOrder || sortOrder;
        return params;
    }, [pageNumber, pageSize, sortBy, sortOrder]);

    const applyResponse = useCallback((response) => {
        const content = response?.content ?? (Array.isArray(response) ? response : []);
        setData(content);
        setTotalPages(response?.totalPages ?? 0);
        setTotalElements(response?.totalElements ?? content.length);
    }, []);

    const getData = useCallback(async (params = {}) => {
        const apiParams = buildNoiNotifiesParams(params.searchQuery || {}, electronicRecordType);
        const response = await fetchNoiNotifies(apiParams);
        applyResponse(response);
        return response?.content ?? (Array.isArray(response) ? response : []);
    }, [electronicRecordType, buildNoiNotifiesParams, applyResponse]);

    const handleSearchClick = useCallback((getDataFunc, params) => {
        setPageNumber(0);
        const apiParams = buildNoiNotifiesParams(params?.searchQuery || searchQuery, electronicRecordType, { page: 0 });
        fetchNoiNotifies(apiParams).then(applyResponse);
    }, [buildNoiNotifiesParams, searchQuery, electronicRecordType, applyResponse]);

    const loadData = useCallback(async () => {
        const apiParams = buildNoiNotifiesParams(searchQuery, electronicRecordType);
        const response = await fetchNoiNotifies(apiParams);
        applyResponse(response);
    }, [searchQuery, electronicRecordType, buildNoiNotifiesParams, applyResponse]);

    const handleFormationClick = () =>
        formationNoiNotify(loadData, { defaultErrorMessage, excludedType });

    const handleReportClick = () => openReport(selectedRecord?.parId);

    const handleApproveDocClick = () =>
        approveNoiNotify(selectedRecord?.parId, { defaultErrorMessage });

    const handleGroupApproveClick = () =>
        confirmNoiNotify(selectedRecord?.parId, { defaultErrorMessage });

    const handleFileSaveClick = () =>
        exportNoiNotify(selectedRecord?.parId, { defaultErrorMessage });

    const handleNraReportsClick = () => navigateToNraReportsTable(navigate);

    const HANDLERS = {
        formation: handleFormationClick,
        printer: handleReportClick,
        approveDoc: handleApproveDocClick,
        fileSave: handleFileSaveClick,
        groupApprove: handleGroupApproveClick,
        report: handleNraReportsClick,
    };

 

    const actionButtons = (
        <ActionButtons
            buttons={icons.map(icon => {
                const isPrinterLoading = icon.id === 'printer' && reportLoading;
                return {
                    type: icon.id,
                    ...(isPrinterLoading ? { icon: faSpinner, iconProps: { spin: true } } : { iconImg: icon.image, iconProps: { style: { width: 28, height: 28 } } }),
                    title: isPrinterLoading ? 'common.loading' : icon.labelKey,
                    showWhen: 'always',
                    disabled: isPrinterLoading,
                    onClick: () => HANDLERS[icon.id]?.()
                };
            })}
        />
    )


    useEffect(() => {
        loadData();
    }, [loadData])


    const columns = [
        { key: 'parId', label: t('staff.register.nra.batchNumber') },
        { key: 'parDate', label: t('staff.register.nra.date') },
        { key: 'note', label: t('staff.register.nra.note') },
        { key: 'status', label: t('staff.register.nra.status') },
        { key: 'branchName', label: t('staff.register.nra.companyBranch') },
        { key: 'userFullName', label: t('staff.register.nra.user') }
    ]

    const electronicRecordTypeOptions = [
        { value: '62', label: 'Електронен трудов запис по чл. 62, ал. 5  от Кодекса на труда' },
        { value: '123', label: 'Електронен трудов запис по чл. 123, ал.1 от Кодекса на труда' },
    ];

    const searchOptions = [
        {
            key: 'report-saved', label: 'reportSaved', type: 'select', options: [
                { value: 'yes', labelKey: 'common.yes' },
                { value: 'no', labelKey: 'common.no' }
            ]
        },
        { key: 'fromDate', label: 'fromDate', type: 'date' },
        { key: 'toDate', label: 'toDate', type: 'date' },
        {
            key: 'status',
            label: 'status',
            type: 'select',
            defaultValue: 'staff.register.nra.statusAll',
            options: [
                { value: 'staff.register.nra.statusAll', labelKey: 'staff.register.nra.statusAll' },
                { value: 'staff.register.nra.statusPreparing', labelKey: 'staff.register.nra.statusPreparing' },
                { value: 'staff.register.nra.statusSubmitted', labelKey: 'staff.register.nra.statusSubmitted' }
            ],
        },
    ]

    return (
        <>
            <Table
                title={t('staff.register.nra.title')}
                actionButtons={actionButtons}
                columns={columns}
                data={data}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getData={getData}
                handleSearchClick={handleSearchClick}
                searchBarProps={{
                    topSelect: {
                        value: electronicRecordType,
                        onChange: setElectronicRecordType,
                        options: electronicRecordTypeOptions,
                    },
                }}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalPages={totalPages}
                totalElements={totalElements}
                setpageNumber={setPageNumber}
                setPageSize={setPageSize}
                handleRowClick={handleRowClick}
                selectedData={selectedRecord?.parId}
                checkboxField="parId"
            />
        </>
    );
}